import './style.css';
import '../../animation/animation-slide.css';

import { isObservableArray } from 'mobx';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import ReactCSSTransitionReplace from 'react-css-transition-replace';
import validate from 'validate.js';
import Colours from '../../../styles/colours';
import ClinicalLookup from '../../clinical/clinical-lookup';
import ClinicalNumeric from '../../clinical/clinical-numeric';
import ClinicalText from '../../clinical/clinical-text';
import Field from '../../form/field/field';
import FieldDatePicker from '../../form/field/field-date-picker';
import FieldInteger from '../../form/field/field-integer';
import FieldSelect from '../../form/field/field-select';
import Button from '../../general/button';
import DateLabel from '../../general/date/date-label';
import TimeLabel from '../../general/date/time-label';
import OutlineButton from '../../general/outline-button';
import Text from '../../general/text';
import Show from '../../layout/show';
import { TabCard } from '../../ux/tab-card';

const alignment = (column) => {
  if (['text', 'textlookup'].indexOf(column.type) < 0) return 'align-left';
};

const ReadOnlyRenderers = {
  date: (value) => <DateLabel skipUtc={true} value={value} />,
  datetime: (value) => (
    <span>
      <DateLabel value={value} skipUtc={true} />
      &nbsp;
      <TimeLabel value={value} skipUtc={true} />
    </span>
  ),
  lookup: (value) => <span>{value}</span>,
  text: (value) => <span>{value}</span>,
  textlookup: (value) => <span>{value}</span>,
  integer: (value) => <span>{value}</span>,
  numeric: (value, unit) => (
    <span>
      <span>{value}</span>
      &nbsp;
      <span>{unit}</span>
    </span>
  )
};

const EditRenderers = {
  date: ({ value, onChange, label, ...other }) => (
    <FieldDatePicker
      value={value}
      onChange={(e) => {
        onChange(e);
      }}
      label={label}
      {...other}
    />
  ),
  datetime: ({ value, onChange, label, ...other }) => (
    <FieldDatePicker
      value={value}
      onChange={(e) => {
        onChange(e);
      }}
      label={label}
      {...other}
    />
  ),
  lookup: ({
    value,
    onChange,
    label,
    errored,
    warning,
    src,
    lookups,
    searches,
    lists,
    cache,
    onAction,
    ...other
  }) => (
    <ClinicalLookup
      errored={errored}
      warning={warning}
      value={value}
      onChange={onChange}
      label={label}
      src={src}
      inline={false}
      full-lookup-path={true}
      lookups={lookups}
      searches={searches}
      lists={lists}
      cache={cache}
      onAction={onAction}
      {...other}
    />
  ),
  text: ({ value, onChange, label, ...other }) => (
    <Field value={value} onChange={onChange} label={label} {...other} />
  ),
  textlookup: ({
    value,
    onChange,
    label,
    mode,
    errored,
    warning,
    src,
    cache,
    onAction,
    ...other
  }) => (
    <ClinicalText
      errored={errored}
      warning={warning}
      value={value || ''}
      onChange={onChange}
      label={label}
      mode={mode}
      src={src}
      inline={false}
      full-lookup-path={true}
      appendMode={'Space'}
      cache={cache}
      onAction={onAction}
      {...other}
    />
  ),
  integer: ({ value, onChange, label, ...other }) => (
    <FieldInteger value={value} onChange={onChange} label={label} {...other} />
  ),
  numeric: ({ value, onChange, label, src, unit, errored, warning, onUnitChange, ...other }) => (
    <ClinicalNumeric
      errored={errored}
      warning={warning}
      value={value}
      onChange={onChange}
      label={label}
      src={src}
      unit={unit}
      onUnitChange={onUnitChange}
      {...other}
    />
  )
};

class Table extends Component {
  state = {
    editMode: false,
    activeRow: {},
    addMode: false,
    transition: 'slide-left',
    submitted: false,
    errors: {}
  };

  constructor(props) {
    super(props);

    this.onEditRow = this.onEditRow.bind(this);
    this.onRemoveRow = this.onRemoveRow.bind(this);
    this.onChangeRow = this.onChangeRow.bind(this);
    this.onRequestAddRow = this.onRequestAddRow.bind(this);
    this.onCancelAddRow = this.onCancelAddRow.bind(this);
    this.onCurrentChange = this.onCurrentChange.bind(this);
    this.onCurrentUnitChange = this.onCurrentUnitChange.bind(this);
  }

  onEditRow(row) {
    const { value } = this.props;
    const activeRow = Object.assign({}, row);
    const activeIndex = value.indexOf(row);
    const editMode = true;
    this.setState({
      editMode,
      activeRow,
      activeIndex,
      transition: 'slide-left'
    });
  }

  onRemoveRow(row) {
    const { value, onChange } = this.props;
    const { activeIndex } = this.state;
    const item = value[activeIndex];
    value.splice(activeIndex);
    if (onChange) {
      onChange(value, item);
    }
    this.setState({
      activeRow: undefined,
      addMode: false,
      editMode: false,
      transition: 'slide-left',
      submitted: false,
      errors: {}
    });
  }

  onChangeRow(adding) {
    let { value, onChange } = this.props;
    const { activeRow, activeIndex } = this.state;
    const errors = validate(activeRow, this.props.validation || {});

    if (errors && Object.keys(errors).length > 0) {
      return this.setState({ errors, submitted: true });
    }
    value = value || [];
    if (adding) {
      value.push(activeRow);
    } else {
      Object.assign(value[activeIndex], activeRow);
    }
    if (onChange) {
      onChange(value, value[value.length - 1]);
    }
    this.setState({
      activeRow: undefined,
      addMode: false,
      editMode: false,
      transition: 'slide-left',
      submitted: false,
      errors: {}
    });
  }

  onRequestAddRow() {
    let activeRow = {};
    const { onGetDefaultForNewItem } = this.props;
    if (onGetDefaultForNewItem) {
      activeRow = onGetDefaultForNewItem();
    }
    const addMode = true;
    this.setState({
      addMode,
      activeRow,
      activeIndex: -1,
      transition: 'slide-left',
      submitted: false,
      errors: {}
    });
  }

  onCancelAddRow() {
    this.setState({
      editMode: false,
      addMode: false,
      activeRow: undefined,
      activeIndex: -1,
      transition: 'slide-right',
      submitted: false,
      errors: {}
    });
  }

  onCurrentChange(key, value) {
    const { activeRow } = this.state;
    activeRow[key] = value;
    this.setState({ activeRow });
  }

  onCurrentUnitChange(value) {
    const { activeRow } = this.state;
    activeRow['unit'] = value;
    this.setState({ activeRow });
  }

  render() {
    let {
      columns,
      canAdd,
      canEdit,
      canRemove,
      value,
      emptyMessage,
      readOnly,
      label,
      erroredActivity,
      erroredFrequency,
      erroredIntensity,
      warnedActivity,
      warnedFrequency,
      warnedIntensity,
      lookups,
      lists,
      searches,
      cache,
      onAction
    } = this.props;

    const {
      onEditRow,
      onRemoveRow,
      onChangeRow,
      onRequestAddRow,
      onCancelAddRow,
      onCurrentChange,
      onCurrentUnitChange
    } = this;
    const { activeRow, editMode, addMode, transition, errors, submitted } = this.state;
    canAdd = canAdd && !readOnly;
    canEdit = canEdit && !readOnly;
    let content = null;
    if (typeof value === 'string' && value !== '') value = JSON.parse(value);
    if (editMode) {
      content = (
        <EditView
          key="edit"
          {...{
            columns,
            errors,
            activeRow,
            onCancelAddRow,
            onChangeRow,
            onRemoveRow,
            onCurrentChange,
            onCurrentUnitChange,
            canRemove,
            submitted,
            lookups,
            lists,
            searches,
            cache,
            onAction
          }}
        />
      );
    } else if (addMode) {
      content = (
        <EditView
          key="add"
          {...{
            add: true,
            errors,
            columns,
            activeRow,
            onCancelAddRow,
            onChangeRow,
            onRemoveRow: () => undefined,
            onCurrentChange,
            onCurrentUnitChange,
            canRemove,
            submitted,
            lookups,
            lists,
            searches,
            cache,
            onAction
          }}
        />
      );
    } else {
      content = (
        <TableView
          key="table"
          {...{
            columns,
            canAdd,
            canEdit,
            canRemove,
            value,
            onEditRow,
            onRemoveRow,
            onChangeRow,
            onRequestAddRow,
            emptyMessage,
            lookups,
            lists,
            searches,
            cache,
            onAction
          }}
        />
      );
    }

    return (
      <div>
        <Show if={!this.props.hideLabel}>
          <span style={{ color: Colours.lightBlue, fontSize: 16, display: 'block' }}>{label}</span>
        </Show>
        <ReactCSSTransitionReplace
          transitionName={transition}
          transitionEnterTimeout={400}
          transitionLeaveTimeout={400}
          className="bz-table-editor"
        >
          {content}
        </ReactCSSTransitionReplace>
      </div>
    );
  }
}

const EditView = ({
  add = false,
  columns = [],
  activeRow,
  onChangeRow,
  onCancelAddRow,
  onRemoveRow,
  onCurrentChange,
  onCurrentUnitChange,
  canRemove,
  errors,
  submitted,
  searches,
  lists,
  lookups,
  cache,
  onAction
}) => (
  <TabCard header={add ? 'Add Entry' : 'Edit Entry'}>
    <div>
      {columns.map((column, i) => {
        const onChange = (e, value) => {
          if (e.target) {
            onCurrentChange(column.field, e.target.value);
          } else if (typeof e === 'string') {
            onCurrentChange(column.field, e);
          } else {
            onCurrentChange(column.field, value || e);
          }
        };

        const onUnitChange = (e, value) => {
          onCurrentUnitChange(value || e);
        };

        const Editor = EditRenderers[column.type];
        const fieldErrors = errors[column.field];
        let errorText = undefined;
        if (fieldErrors && fieldErrors.length > 0) {
          errorText = fieldErrors[0];
        }
        return (
          <Editor
            key={i}
            {...{
              value: activeRow[column.field],
              onChange,
              errorText,
              submitted,
              label: column.label,
              mode: column.mode,
              src: column.src,
              unit: activeRow['unit'],
              onUnitChange,
              searches,
              lists,
              lookups,
              cache,
              onAction
            }}
          />
        );
      })}
      <hr />
      <Show if={!add && canRemove} className="float-left">
        <OutlineButton
          onClick={() => onRemoveRow(activeRow)}
          style={{ border: Colours.red, background: Colours.red200 }}
        >
          Remove
        </OutlineButton>
      </Show>
      <div className="float-right">
        <OutlineButton onClick={onCancelAddRow} style={{ border: 'none', background: 'white' }}>
          Cancel
        </OutlineButton>
        <OutlineButton onClick={() => onChangeRow(add)}>{add ? 'Add' : 'Update'}</OutlineButton>
      </div>
    </div>
  </TabCard>
);

const TableView = ({
  emptyMessage,
  columns = [],
  canAdd,
  canEdit,
  canRemove,
  value = [],
  onEditRow,
  onRemoveRow,
  onRequestAddRow
}) => {
  let add = null;

  if (canAdd) {
    add = (
      <div className="align-centre">
        <Button onClick={onRequestAddRow} label="Add Entry" small={true} />
      </div>
    );
  }

  return (
    <div>
      <Show if={value && value.length > 0}>
        <table>
          <thead>
            <tr>
              {columns.map((column, i) => (
                <th key={i} className={alignment(column)}>
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(value || []).map((row, i) => (
              <TableRow
                key={i}
                {...{
                  row,
                  columns,
                  canEdit,
                  onEditRow,
                  canRemove,
                  onRemoveRow
                }}
              />
            ))}
          </tbody>
        </table>
      </Show>
      <Show if={!value || value.length === 0}>{emptyMessage}</Show>
      {add}
    </div>
  );
};

const TableRow = ({ row, columns = [], canEdit, onEditRow }) => {
  let edit = null;
  if (canEdit) {
    edit = (
      <td style={{ textAlign: 'center' }}>
        <a onClick={() => onEditRow(row)}>Edit</a>
      </td>
    );
  }
  return (
    <tr>
      {columns.map((column, i) => (
        <TableCell key={i} column={column} row={row} className={'align-left'} />
      ))}
      {edit}
    </tr>
  );
};

const TableCell = ({ column, row, className }) => {
  return (
    <td width={column.width} className={className}>
      {ReadOnlyRenderers[column.type](row[column.field], row['unit'])}
    </td>
  );
};
Table.defaultProps = {
  value: [],
  validation: {},
  columns: [{ type: '', label: '' }],
  editableMember: 'canEdit', //represents per-row-can-edit property on value
  canEdit: true,
  canAdd: true,
  canRemove: false,
  onChange: () => undefined,
  onUnitChange: () => undefined
};

Table.propTypes = {
  value: PropTypes.any,
  validation: PropTypes.any,
  columns: PropTypes.array,
  canAdd: PropTypes.bool,
  canEdit: PropTypes.bool,
  canRemove: PropTypes.bool,
  onGetDefaultForNewItem: PropTypes.func,
  emptyMessage: PropTypes.any
};

const TableJson = (props) => {
  let val = props.value || '[]';
  let valueProp = [];

  if (!isObservableArray(val)) {
    try {
      valueProp = JSON.parse(val.replace(/\\n/gm, '\n').replace(/\n/gm, '\\n'));
    } catch (ex) {}
  } else {
    valueProp = val;
  }

  return (
    <Table
      {...props}
      value={valueProp}
      onChange={(value) => props.onChange(JSON.stringify(value))}
    />
  );
};

export default Table;

export const { defaultProps, propTypes } = Table;

export { TableJson };
